import { escapeHTML, formatBytes, renderSkeleton, timeAgo } from "../render/ui-helpers.js";
import {
  joinWorkspacePath,
  parentWorkspacePath,
  workspaceBasename
} from "./state.js";

function shortCommit(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.length <= 8 ? raw : raw.slice(0, 8);
}

function renderWorkspaceTreeEntry(workspaceID, panel, entry) {
  const isDir = entry.kind === "dir";
  const isSelected = !isDir && panel.selectedFilePath === entry.path;
  const modeLabel = entry.mode === "100755" ? "+x" : "";
  const meta = [modeLabel, typeof entry.size === "number" ? formatBytes(entry.size) : "", entry.binary ? "binary" : ""]
    .filter(Boolean)
    .join(" · ");
  return `
    <button class="workspace-tree-entry${isSelected ? " workspace-tree-entry--selected" : ""}" type="button" data-action="open-workspace-entry" data-workspace-id="${escapeHTML(workspaceID)}" data-workspace-kind="${escapeHTML(entry.kind || "")}" data-workspace-path="${escapeHTML(entry.path || "")}">
      <span class="workspace-tree-entry__icon">${isDir ? "\u25B8" : "\u00B7"}</span>
      <span class="workspace-tree-entry__label${isDir ? " workspace-tree-entry__label--dir" : ""}">${escapeHTML(workspaceBasename(entry.path || ""))}</span>
      ${meta ? `<span class="workspace-tree-entry__meta">${escapeHTML(meta)}</span>` : ""}
    </button>
  `;
}

function renderWorkspaceFilePreview(file, panel) {
  const previewModeLabel = file.mode === "100755" ? "executable" : "";
  const meta = [
    previewModeLabel,
    typeof file.size === "number" ? formatBytes(file.size) : "",
    file.content_type || "",
    file.binary ? "binary" : "text"
  ].filter(Boolean);
  return `
    <div class="workspace-file-preview">
      <div class="workspace-file-preview__meta">${escapeHTML(meta.join(" \u00B7 "))}</div>
      ${file.sha256 ? `<div class="entity-row__detail">${escapeHTML(file.sha256)}</div>` : ""}
      ${file.content != null ? (panel && panel.fileEditing
        ? `<textarea class="code-block code-block--edit" name="file_edit_content">${escapeHTML(panel.fileDraftContent != null ? panel.fileDraftContent : file.content)}</textarea>`
        : `<pre class="code-block">${escapeHTML(file.content)}</pre>`
      ) : `
        <div class="empty">
          ${file.binary ? `<span class="badge">${escapeHTML(file.content_type || "binary")}</span> ${typeof file.size === "number" ? escapeHTML(formatBytes(file.size)) : ""}` : escapeHTML(file.content_omitted_reason || "File content is not available for inline preview.")}
        </div>
      `}
    </div>
  `;
}

function workspaceContentBreadcrumbs(workspaceID, currentPath) {
  const current = String(currentPath || "");
  if (!current) return "";
  const parts = current.split("/");
  return parts.map((part, index) => {
    const nextPath = parts.slice(0, index + 1).join("/");
    return `<button class="workspace-content-breadcrumbs__item" type="button" data-action="open-workspace-path" data-workspace-id="${escapeHTML(workspaceID)}" data-workspace-path="${escapeHTML(nextPath)}">${escapeHTML(part)}</button>`;
  }).join("");
}

function renderMutationStrip(item, panel) {
  const active = panel.activeMutation;
  if (!active) return "";
  const wid = escapeHTML(item.id);
  const defaultFileTarget = panel.selectedFilePath || (panel.fileUploadName ? joinWorkspacePath(panel.path, panel.fileUploadName) : "");
  const defaultDeleteTarget = panel.selectedFilePath || panel.path || "";
  const note = `Commits on ${escapeHTML(item.default_branch || "main")}${panel.commit ? ` \u00B7 base ${escapeHTML(shortCommit(panel.commit))}` : ""}`;

  if (active === "file-upload") {
    return `
      <div class="wc-mutation-strip">
        <div class="wc-mutation-strip__header">
          <div class="mini-label">File Upload</div>
          <span class="entity-row__detail">${note}</span>
        </div>
        <div class="wc-mutation-form">
          <label class="field">
            <span>Target path</span>
            <input name="content_file_upload_path" value="${escapeHTML(panel.fileUploadPath || "")}" placeholder="${escapeHTML(defaultFileTarget || "<dir>/<filename>")}">
          </label>
          <label class="field">
            <span>File</span>
            <input type="file" name="content_file_upload_file">
            ${panel.fileUploadName ? `<div class="entity-row__detail">${escapeHTML(panel.fileUploadName)}</div>` : ""}
          </label>
          <label class="checkbox">
            <input type="checkbox" name="content_file_upload_executable" ${panel.fileUploadExecutable ? "checked" : ""}>
            <span>Executable</span>
          </label>
          <button class="button button--primary button--sm" type="button" data-action="submit-workspace-file-upload" data-workspace-id="${wid}" ${panel.fileUploading ? "disabled" : ""}>${panel.fileUploading ? "Uploading..." : (panel.selectedFilePath ? "Replace" : "Upload")}</button>
        </div>
        ${panel.fileUploading ? `<div class="workspace-import-actions"><div class="workspace-import-progress"><div class="workspace-import-progress__bar" style="width:${Math.max(4, panel.fileUploadProgress || 0)}%"></div></div><span class="entity-row__detail">${escapeHTML(`${Math.round(panel.fileUploadProgress || 0)}%`)}</span></div>` : ""}
      </div>`;
  }

  if (active === "tree-upload") {
    return `
      <div class="wc-mutation-strip">
        <div class="wc-mutation-strip__header">
          <div class="mini-label">Tree Upload</div>
          <span class="entity-row__detail">${note}</span>
        </div>
        <div class="wc-mutation-form">
          <label class="field">
            <span>Target directory</span>
            <input name="content_tree_upload_path" value="${escapeHTML(panel.treeUploadPath || "")}" placeholder="${escapeHTML(panel.path || "/")}">
          </label>
          <label class="field">
            <span>Zip archive</span>
            <input type="file" name="content_tree_upload_archive" accept=".zip,application/zip">
            ${panel.treeUploadName ? `<div class="entity-row__detail">${escapeHTML(panel.treeUploadName)}</div>` : ""}
          </label>
          <button class="button button--primary button--sm" type="button" data-action="submit-workspace-tree-upload" data-workspace-id="${wid}" ${panel.treeUploading ? "disabled" : ""}>${panel.treeUploading ? "Uploading..." : "Upload tree"}</button>
        </div>
        ${panel.treeUploading ? `<div class="workspace-import-actions"><div class="workspace-import-progress"><div class="workspace-import-progress__bar" style="width:${Math.max(4, panel.treeUploadProgress || 0)}%"></div></div><span class="entity-row__detail">${escapeHTML(`${Math.round(panel.treeUploadProgress || 0)}%`)}</span></div>` : ""}
      </div>`;
  }

  if (active === "delete") {
    return `
      <div class="wc-mutation-strip wc-mutation-strip--danger">
        <div class="wc-mutation-strip__header">
          <div class="mini-label">Delete Path</div>
          <span class="entity-row__detail">${note}</span>
        </div>
        <div class="wc-mutation-form">
          <label class="field">
            <span>Canonical path</span>
            <input name="content_delete_path" value="${escapeHTML(panel.deletePath || "")}" placeholder="${escapeHTML(defaultDeleteTarget || "selected file or directory")}">
          </label>
          <button class="button button--ghost button--sm" type="button" data-action="submit-workspace-delete-path" data-workspace-id="${wid}" ${panel.deletingPath ? "disabled" : ""}>${panel.deletingPath ? "Deleting..." : "Delete"}</button>
        </div>
      </div>`;
  }

  return "";
}

function renderDropBanner(panel) {
  if (panel.dropPendingFiles && panel.dropPendingFiles.length > 0) {
    const files = panel.dropPendingFiles;
    const count = files.length;
    const dest = escapeHTML(panel.path || "/");
    const fileListHTML = Array.from(files).map((f, i) =>
      `<li><span class="wc-drop-confirm__name">${escapeHTML(f.name)}</span><span class="wc-drop-confirm__size">${formatBytes(f.size)}</span><button class="wc-drop-confirm__remove" type="button" data-action="remove-drop-file" data-drop-index="${i}" title="Remove">\u00D7</button></li>`
    ).join("");
    return `
      <div class="wc-drop-confirm">
        <div class="wc-drop-confirm__header">
          <span>Upload ${count} file${count === 1 ? "" : "s"} into <strong>${dest}</strong></span>
          <div class="wc-feedback__actions">
            <button class="button button--sm button--primary" type="button" data-action="confirm-drop-upload">Upload</button>
            <button class="button button--sm button--ghost" type="button" data-action="cancel-drop-upload">Cancel</button>
          </div>
        </div>
        <ul class="wc-drop-confirm__list">${fileListHTML}</ul>
      </div>`;
  }
  if (!panel.dropUploading) return "";
  const done = panel.dropUploadDone || 0;
  const total = panel.dropUploadTotal || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return `
    <div class="wc-feedback wc-feedback--busy">
      <span>Uploading ${done}/${total} files\u2026</span>
      <div class="workspace-import-progress" style="flex:1;max-width:12rem"><div class="workspace-import-progress__bar" style="width:${Math.max(4, pct)}%"></div></div>
    </div>`;
}

function renderFeedbackBanner(panel) {
  const dropBanner = renderDropBanner(panel);
  if (dropBanner) return dropBanner;
  if (panel.feedbackDismissed) return "";
  if (panel.mutationError) {
    return `
      <div class="wc-feedback wc-feedback--error">
        <span>${escapeHTML(panel.mutationError)}</span>
        <button class="button button--sm button--ghost" type="button" data-action="dismiss-feedback">Dismiss</button>
      </div>`;
  }
  const change = panel.lastChange;
  if (!change || !change.result_commit) return "";
  const details = [
    change.operation || "",
    change.committed ? "committed" : "no-op",
    change.path || "",
    change.result_commit ? shortCommit(change.result_commit) : ""
  ].filter(Boolean);
  return `
    <div class="wc-feedback">
      <span>${escapeHTML(details.join(" \u00B7 "))}${change.commit_message ? ` \u2014 ${escapeHTML(change.commit_message)}` : ""}</span>
      <button class="button button--sm button--ghost" type="button" data-action="dismiss-feedback">Dismiss</button>
    </div>`;
}

function renderWorkspaceImportStatus(record) {
  if (!record || !record.id) return "";
  const details = [
    record.package_filename || "",
    record.result_commit ? `result ${shortCommit(record.result_commit)}` : "",
    record.base_commit ? `base ${shortCommit(record.base_commit)}` : "",
    typeof record.bytes === "number" ? formatBytes(record.bytes) : "",
    typeof record.file_count === "number" && record.file_count > 0 ? `${record.file_count} file${record.file_count === 1 ? "" : "s"}` : ""
  ].filter(Boolean);
  const timestamps = [
    record.started_at ? `started ${timeAgo(record.started_at)}` : "",
    record.finished_at ? `finished ${timeAgo(record.finished_at)}` : ""
  ].filter(Boolean).join(" \u00B7 ");
  return `
    <div class="workspace-import-status workspace-import-status--${escapeHTML(record.state || "unknown")}">
      <div class="workspace-import-status__header">
        <div>
          <div class="workspace-import-status__title">Last import</div>
          <div class="workspace-import-status__meta">${escapeHTML([record.state || "unknown", record.mode || "", record.format || ""].filter(Boolean).join(" \u00B7 "))}</div>
        </div>
        ${record.committed ? `<span class="badge">committed</span>` : ""}
      </div>
      ${details.length ? `<div class="entity-row__detail">${escapeHTML(details.join(" \u00B7 "))}</div>` : ""}
      ${timestamps ? `<div class="entity-row__detail">${escapeHTML(timestamps)}</div>` : ""}
      ${record.commit_message ? `<div class="entity-row__detail">${escapeHTML(record.commit_message)}</div>` : ""}
      ${record.error_message ? `<div class="form-error">${escapeHTML(record.error_message)}</div>` : ""}
    </div>
  `;
}

export function renderWorkspaceContentPanel(state, workspaceItem, options = {}) {
  const showSnapshotReplace = options.showSnapshotReplace === true;
  const panel = state;
  const selectedFile = panel.file;
  const resolvedRef = panel.ref || workspaceItem.default_branch || "default";
  const activeMutation = panel.activeMutation;
  const wid = escapeHTML(workspaceItem.id);

  let snapshotHTML = "";
  if (showSnapshotReplace) {
    const importNotice = workspaceItem.remote_connection_state === "connected"
      ? "Import creates a new local commit on the canonical default branch. Push it to publish the snapshot to the remote."
      : "Import replaces the canonical workspace snapshot and records one auditable local commit.";
    snapshotHTML = `
      <details class="disclosure">
        <summary>Advanced Snapshot Replace</summary>
        <div class="disclosure__body">
          <div class="form-note form-note--warn">Replace-snapshot import removes canonical files that are absent from the package. It does not edit live thread worktrees. ${escapeHTML(importNotice)}</div>
          <div class="workspace-import-grid">
            <label class="field">
              <span>Package</span>
              <input type="file" name="import_package" accept=".zip,application/zip">
              ${panel.importFileName ? `<div class="entity-row__detail">${escapeHTML(panel.importFileName)}</div>` : `<div class="entity-row__detail">Choose a .zip snapshot exported from the canonical repo.</div>`}
            </label>
            <label class="field">
              <span>Commit message override</span>
              <input name="import_message" value="${escapeHTML(panel.importMessage || "")}" placeholder="Optional commit message">
            </label>
            <label class="field">
              <span>Expected base commit</span>
              <input name="import_expected_base_commit" value="${escapeHTML(panel.importExpectedBaseCommit || "")}" placeholder="${escapeHTML(workspaceItem.last_verified_target_head ? shortCommit(workspaceItem.last_verified_target_head) : "Optional safety check")}">
            </label>
          </div>
          <div class="workspace-import-actions">
            <button class="button button--primary" type="button" data-action="submit-workspace-import" data-workspace-id="${wid}" ${panel.importing ? "disabled" : ""}>${panel.importing ? "Replacing..." : "Replace from snapshot"}</button>
            ${panel.lastImport?.id ? `<button class="button button--ghost" type="button" data-action="refresh-workspace-import" data-workspace-id="${wid}" data-import-id="${escapeHTML(panel.lastImport.id)}">Refresh status</button>` : ""}
            ${panel.importing ? `<div class="workspace-import-progress"><div class="workspace-import-progress__bar" style="width:${Math.max(4, panel.importProgress || 0)}%"></div></div><span class="entity-row__detail">${escapeHTML(`${Math.round(panel.importProgress || 0)}% uploaded`)}</span>` : ""}
          </div>
          ${panel.importError ? `<div class="form-error">${escapeHTML(panel.importError)}</div>` : ""}
          ${renderWorkspaceImportStatus(panel.lastImport)}
        </div>
      </details>
    `;
  }

  return `
    <div class="wc-toolbar">
      <div class="wc-toolbar__nav">
        <button class="wc-ref-badge" type="button" data-action="toggle-ref-input">${escapeHTML(resolvedRef)}${panel.commit ? ` @ ${escapeHTML(shortCommit(panel.commit))}` : ""}</button>
        <div class="workspace-content-breadcrumbs">
          <button class="workspace-content-breadcrumbs__item" type="button" data-action="open-workspace-root" data-workspace-id="${wid}">/</button>
          ${workspaceContentBreadcrumbs(workspaceItem.id, panel.path)}
        </div>
      </div>
      <div class="wc-toolbar__actions">
        <button class="button button--sm button--ghost${activeMutation === "file-upload" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="file-upload">Upload</button>
        <button class="button button--sm button--ghost${activeMutation === "tree-upload" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="tree-upload">Tree</button>
        <button class="button button--sm button--ghost${activeMutation === "delete" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="delete">Delete</button>
        <button class="button button--sm button--ghost" type="button" data-action="export-workspace-content" data-workspace-id="${wid}" ${panel.exporting ? "disabled" : ""}>${panel.exporting ? "Exporting..." : "Export"}</button>
        <button class="button button--sm button--ghost" type="button" data-action="open-workspace-root" data-workspace-id="${wid}" ${panel.loadingTree ? "disabled" : ""}>Root</button>
      </div>
    </div>
    <div class="wc-ref-input" ${panel.refInputVisible ? "" : "hidden"}>
      <label class="field">
        <span>Ref</span>
        <input name="content_ref" value="${escapeHTML(panel.refInput || "")}" placeholder="${escapeHTML(workspaceItem.default_branch || "main")}">
      </label>
      <button class="button button--sm button--ghost" type="button" data-action="reload-workspace-content" data-workspace-id="${wid}" ${panel.loadingTree ? "disabled" : ""}>Load</button>
    </div>
    ${renderMutationStrip(workspaceItem, panel)}
    ${renderFeedbackBanner(panel)}
    <div class="wc-browser">
      <section class="wc-tree wc-drop-target" data-workspace-id="${wid}">
        <div class="wc-drop-overlay"><span>Drop file to upload</span></div>
        <div class="wc-tree__header">
          <div class="mini-label">Tree</div>
          <div class="workspace-content-breadcrumbs">
            <button class="workspace-content-breadcrumbs__item" type="button" data-action="open-workspace-root" data-workspace-id="${wid}">/</button>
            ${workspaceContentBreadcrumbs(workspaceItem.id, panel.path)}
          </div>
        </div>
        ${panel.treeError ? `<div class="form-error">${escapeHTML(panel.treeError)}</div>` : ""}
        ${panel.loadingTree ? renderSkeleton(3) : panel.entries.length ? `
          <div class="workspace-tree-list">
            ${panel.path ? `<button class="workspace-tree-entry" type="button" data-action="open-workspace-path" data-workspace-id="${wid}" data-workspace-path="${escapeHTML(parentWorkspacePath(panel.path))}"><span class="workspace-tree-entry__icon">\u25B8</span><span class="workspace-tree-entry__label workspace-tree-entry__label--dir">..</span><span class="workspace-tree-entry__meta"></span></button>` : ""}
            ${panel.entries.map((entry) => renderWorkspaceTreeEntry(workspaceItem.id, panel, entry)).join("")}
          </div>
        ` : `<div class="empty">No files found at this path.</div>`}
      </section>
      <section class="wc-preview">
        <div class="wc-preview__header">
          <div>
            <div class="mini-label">Preview</div>
            ${selectedFile ? `<div class="workspace-content-preview__path">${escapeHTML(selectedFile.path || panel.selectedFilePath || "")}</div>` : ""}
          </div>
          ${selectedFile && selectedFile.content != null && !selectedFile.binary ? `
            <div class="wc-preview__actions">
              ${panel.fileEditing ? `
                <button class="button button--sm button--primary" type="button" data-action="save-file-edit" data-workspace-id="${wid}" ${panel.fileSaving ? "disabled" : ""}>${panel.fileSaving ? "Saving..." : "Save"}</button>
                <button class="button button--sm button--ghost" type="button" data-action="cancel-file-edit" ${panel.fileSaving ? "disabled" : ""}>Cancel</button>
              ` : `
                <button class="button button--sm button--ghost" type="button" data-action="start-file-edit">Edit</button>
              `}
            </div>
          ` : ""}
        </div>
        ${panel.fileError ? `<div class="form-error">${escapeHTML(panel.fileError)}</div>` : ""}
        ${panel.loadingFile ? renderSkeleton(3) : selectedFile ? renderWorkspaceFilePreview(selectedFile, panel) : `
          <div class="empty">
            Select a file from the tree to preview.
          </div>
        `}
      </section>
    </div>
    ${snapshotHTML}
  `;
}

export function renderWorkspaceContentPanelCompact(state, workspaceItem, options = {}) {
  const panel = state;
  const selectedFile = panel.file;
  const resolvedRef = panel.ref || workspaceItem.default_branch || "default";
  const wid = escapeHTML(workspaceItem.id);
  const activeMutation = panel.activeMutation;
  const hasFile = selectedFile || panel.loadingFile || panel.fileError;
  const refLabel = resolvedRef + (panel.commit ? ` @ ${shortCommit(panel.commit)}` : "");
  const readOnly = options.readOnly === true;
  const actionsVisible = !readOnly && Boolean(panel.actionsVisible);

  const toolbarHTML = `
    <div class="wcc-toolbar">
      <div class="wcc-toolbar__nav">
        <div class="workspace-content-breadcrumbs">
          <button class="workspace-content-breadcrumbs__item wcc-ref-pill" type="button" data-action="toggle-ref-input" title="${escapeHTML(refLabel)}">${escapeHTML(resolvedRef.length > 12 ? resolvedRef.slice(0, 12) + "\u2026" : resolvedRef)}</button>
          <button class="workspace-content-breadcrumbs__item" type="button" data-action="open-workspace-root" data-workspace-id="${wid}">/</button>
          ${workspaceContentBreadcrumbs(workspaceItem.id, panel.path)}
        </div>
      </div>
      <div class="wcc-toolbar__end">
        ${readOnly ? "" : `<button class="button button--xs button--ghost" type="button" data-action="export-workspace-content" data-workspace-id="${wid}" ${panel.exporting ? "disabled" : ""} title="Download zip">\u2193</button>`}
        ${readOnly ? "" : `<button class="button button--xs button--ghost${actionsVisible ? " button--active" : ""}" type="button" data-action="toggle-wc-actions" title="File actions">\u2026</button>`}
      </div>
    </div>
  `;

  const refInputHTML = `
    <div class="wc-ref-input" ${panel.refInputVisible ? "" : "hidden"}>
      <label class="field">
        <span>Ref</span>
        <input name="content_ref" value="${escapeHTML(panel.refInput || "")}" placeholder="${escapeHTML(workspaceItem.default_branch || "main")}">
      </label>
      <button class="button button--sm button--ghost" type="button" data-action="reload-workspace-content" data-workspace-id="${wid}" ${panel.loadingTree ? "disabled" : ""}>Load</button>
    </div>
  `;

  const actionsStripHTML = actionsVisible ? `
    <div class="wcc-actions-strip">
      <button class="button button--xs button--ghost${activeMutation === "file-upload" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="file-upload">Upload</button>
      <button class="button button--xs button--ghost${activeMutation === "tree-upload" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="tree-upload">Tree</button>
      <button class="button button--xs button--ghost${activeMutation === "delete" ? " button--active" : ""}" type="button" data-action="set-active-mutation" data-mutation="delete">Delete</button>
      <button class="button button--xs button--ghost" type="button" data-action="open-workspace-root" data-workspace-id="${wid}" ${panel.loadingTree ? "disabled" : ""}>Root</button>
    </div>
  ` : "";

  let bodyHTML = "";
  if (hasFile) {
    const fileName = workspaceBasename(selectedFile?.path || panel.selectedFilePath || "");
    bodyHTML = `
      <div class="wcc-file-view">
        <div class="wcc-file-header">
          <button class="button button--xs button--ghost" type="button" data-action="back-to-tree">\u2190 Back</button>
          <span class="wcc-file-name" title="${escapeHTML(selectedFile?.path || panel.selectedFilePath || "")}">${escapeHTML(fileName)}</span>
          ${!readOnly && selectedFile && selectedFile.content != null && !selectedFile.binary ? `
            <div class="wcc-file-actions">
              ${panel.fileEditing ? `
                <button class="button button--xs button--primary" type="button" data-action="save-file-edit" data-workspace-id="${wid}" ${panel.fileSaving ? "disabled" : ""}>${panel.fileSaving ? "Saving\u2026" : "Save"}</button>
                <button class="button button--xs button--ghost" type="button" data-action="cancel-file-edit" ${panel.fileSaving ? "disabled" : ""}>Cancel</button>
              ` : `
                <button class="button button--xs button--ghost" type="button" data-action="start-file-edit">Edit</button>
              `}
            </div>
          ` : ""}
        </div>
        ${panel.fileError ? `<div class="form-error">${escapeHTML(panel.fileError)}</div>` : ""}
        ${panel.loadingFile ? renderSkeleton(3) : selectedFile ? renderWorkspaceFilePreview(selectedFile, panel) : ""}
      </div>
    `;
  } else {
    bodyHTML = `
      <div class="wcc-tree-view wc-drop-target" data-workspace-id="${wid}">
        <div class="wc-drop-overlay"><span>Drop file to upload</span></div>
        ${panel.treeError ? `<div class="form-error">${escapeHTML(panel.treeError)}</div>` : ""}
        ${panel.loadingTree ? renderSkeleton(3) : panel.entries.length ? `
          <div class="workspace-tree-list">
            ${panel.path ? `<button class="workspace-tree-entry" type="button" data-action="open-workspace-path" data-workspace-id="${wid}" data-workspace-path="${escapeHTML(parentWorkspacePath(panel.path))}"><span class="workspace-tree-entry__icon">\u25B8</span><span class="workspace-tree-entry__label workspace-tree-entry__label--dir">..</span><span class="workspace-tree-entry__meta"></span></button>` : ""}
            ${panel.entries.map((entry) => renderWorkspaceTreeEntry(workspaceItem.id, panel, entry)).join("")}
          </div>
        ` : `<div class="empty">No files at this path.</div>`}
      </div>
    `;
  }

  return `
    ${toolbarHTML}
    ${refInputHTML}
    ${actionsStripHTML}
    ${readOnly ? "" : renderMutationStrip(workspaceItem, panel)}
    ${readOnly ? "" : renderFeedbackBanner(panel)}
    ${bodyHTML}
  `;
}
